<template>
	<div class="webhooks">
		<header>
			<BackToSettings />
			<h1>{{ $t('settings_webhooks_title') }}</h1>
			<div class="header-spacer" />
		</header>

		<section>
			<!-- Loading the embedded portal (mint + iframe load): one spinner,
			     identical to the portal overlay for a continuous feel -->
			<div v-if="state === CHECKING" class="portal-loading-cover">
				<Spinner />
			</div>

			<!-- Not provisioned yet: empty state -->
			<div v-else-if="state === NEEDS_ENABLE" class="centered">
				<WebhookIcon type="duotone" class="webhook-glyph" />
				<h2 v-t="'settings_webhooks_empty_title'" />
				<p class="subtitle" v-t="'settings_webhooks_empty_subtitle'" />
				<Button pronto @click="enableWebhooks">
					{{ $t('settings_webhooks_enable_button') }}
				</Button>
			</div>

			<!-- Provisioning in progress (first-time server setup) -->
			<div v-else-if="state === PROVISIONING" class="centered">
				<div class="loading-graphic">
					<div class="ring" />
					<ScrewdriverWrenchIcon type="duotone" class="tool-glyph" />
				</div>
				<h2 v-t="'settings_webhooks_provisioning_title'" />
				<p class="subtitle" v-t="'settings_webhooks_provisioning_subtitle'" />
			</div>

			<!-- Something went wrong -->
			<div v-else-if="state === ERROR" class="centered">
				<WebhookIcon type="duotone" class="webhook-glyph muted" />
				<h2 v-t="'settings_webhooks_error_title'" />
				<p class="subtitle" v-t="'settings_webhooks_error_subtitle'" />
				<Button pronto @click="retry">
					{{ $t('settings_webhooks_error_retry') }}
				</Button>
			</div>

			<!-- Provisioned: embed the SVIX portal -->
			<div v-show="state === PORTAL" class="portal">
				<div v-if="iframeLoading" class="portal-loading-cover">
					<Spinner />
				</div>
				<iframe
					v-if="portalUrl"
					:src="portalUrl"
					:title="$t('settings_webhooks_title')"
					allow="clipboard-write"
					@load="iframeLoading = false"
				/>
			</div>
		</section>
	</div>
</template>

<script>
import { Button, Spinner, showAlertModal } from '@/components/common'
import { WebhookIcon, ScrewdriverWrenchIcon } from '@/icons'
import BackToSettings from '@/components/MainSettings/BackToSettings'
import store, { getMyUserId } from '@/store'
import { canManageWebhooks } from '@/helpers/permissions'
import { provisionWebhooks, createWebhookPortalSession } from '@/api/user'
import { resolveSchemeAndContrast } from '@/helpers/colors'
import { colorSchemes } from '@/helpers/constants'

const states = {
	CHECKING: 'checking',
	NEEDS_ENABLE: 'needsEnable',
	PROVISIONING: 'provisioning',
	PORTAL: 'portal',
	ERROR: 'error',
}

export default {
	name: 'WebHooks',
	components: {
		Button,
		Spinner,
		WebhookIcon,
		ScrewdriverWrenchIcon,
		BackToSettings,
	},

	data() {
		return {
			...states,
			state: states.CHECKING,
			portalUrl: '',
			iframeLoading: true,
		}
	},

	created() {
		this.init()
	},

	computed: {
		hasEmail() {
			// The authenticated user (RPC user.info shape) carries emails as an
			// array, not a flat `email` field — matches ProfileForm/Feedback/etc.
			return !!store.auth.user?.emails?.[0]?.email
		},
	},

	methods: {
		init() {
			// Deep-link guard: the settings nav hides this page via the same
			// check, but the route itself is reachable by URL.
			if (!canManageWebhooks()) {
				this.$router.replace({
					name: 'mainsettings',
					params: { id: getMyUserId() },
				})
				return
			}
			if (!this.hasEmail) {
				this.state = states.NEEDS_ENABLE
				this.showEmailNeededModal()
				return
			}
			// Always mint on entry — the portal endpoint 404s exactly when the user
			// isn't provisioned, so this is the source of truth on every device
			// (a persisted local flag goes stale when webhooks were enabled in
			// another browser or site data was cleared). Never auto-provisions.
			this.openPortal()
		},

		showEmailNeededModal() {
			showAlertModal({
				title: this.$t('settings_webhooks_email_needed_title'),
				description: this.$t('settings_webhooks_email_needed_desc'),
				danger: false,
			})
		},

		async enableWebhooks() {
			if (!this.hasEmail) {
				this.showEmailNeededModal()
				return
			}
			this.state = states.PROVISIONING
			try {
				await provisionWebhooks()
				await this.openPortal()
			} catch (e) {
				console.error(e)
				if (e?.response?.data?.code === 'WEBHOOKS_EMAIL_REQUIRED') {
					this.state = states.NEEDS_ENABLE
					this.showEmailNeededModal()
				} else {
					this.state = states.ERROR
				}
			}
		},

		async openPortal() {
			this.state = states.CHECKING
			this.iframeLoading = true
			try {
				this.portalUrl = await createWebhookPortalSession(this.portalDarkMode())
				this.state = states.PORTAL
			} catch (e) {
				if (e?.response?.status === 404) {
					// Not provisioned — show the enable state.
					this.state = states.NEEDS_ENABLE
					return
				}
				console.error(e)
				this.state = states.ERROR
			}
		},

		retry() {
			this.init()
		},

		portalDarkMode() {
			// Match the SVIX portal to the appearance theme the app is *currently
			// rendering*. resolveSchemeAndContrast() collapses "System" to the
			// actual dark/light in effect, so a System user on a dark OS still
			// gets the dark portal. API accepts the string enum 'true' | 'false'.
			const { scheme } = resolveSchemeAndContrast()
			return scheme === colorSchemes.DARK ? 'true' : 'false'
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.webhooks {
	$header-size: 60px;
	grid-area: header / header / main / main;

	header {
		height: $header-size;
		padding: 0 32px 0 0;
		border-bottom: 1px solid var(--neutral-40040);
		display: flex;
		align-items: center;
		justify-content: space-between;

		> .pronto-btn {
			position: static !important;
		}

		h1 {
			font-family: 'Avenir-Heavy';
			font-size: 20px;
			color: var(--neutral-800);
		}

		.header-spacer {
			width: 120px;
		}
	}

	section {
		height: 100%;
		position: relative;
		max-height: calc(100% - #{$header-size});
		overflow: hidden;
	}

	// Shared cover for both the portal-mint (checking) and iframe-load spinners so
	// they're the same size/position — one continuous spinner. `:deep` is required
	// to out-specify Spinner's own scoped `font-size` (its default 30px).
	.portal-loading-cover {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;

		:deep(.pronto-spinner) {
			font-size: 48px;
			color: var(--teal-300);
		}
	}

	.centered {
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		text-align: center;
		padding: 0 24px;

		.webhook-glyph {
			font-size: 72px;
			color: var(--teal-300);
			margin-bottom: 24px;

			&.muted {
				color: var(--neutral-300);
			}
		}

		h2 {
			font-family: 'Avenir-Heavy';
			font-size: 18px;
			color: var(--neutral-800);
			margin: 0 0 8px;
		}

		.subtitle {
			font-family: 'Avenir-Medium';
			font-size: 14px;
			color: var(--neutral-500);
			margin: 0 0 24px;
			max-width: 360px;
		}

		// First-time provisioning graphic: screwdriver-wrench inside a spinning
		// track ring (design node 9867:632 "loading-graphic").
		.loading-graphic {
			position: relative;
			width: 100px;
			height: 100px;
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 24px;

			.ring {
				position: absolute;
				width: 76px;
				height: 76px;
				border-radius: 50%;
				border: 4px solid var(--neutral-200);
				border-top-color: var(--teal-300);
				animation: webhooks-provision-spin 1s linear infinite;
			}

			.tool-glyph {
				font-size: 38px;
				color: var(--teal-300);
			}
		}
	}

	@keyframes webhooks-provision-spin {
		to {
			transform: rotate(360deg);
		}
	}

	.portal {
		height: 100%;
		position: relative;

		iframe {
			width: 100%;
			height: 100%;
			border: 0;
		}
	}
}
</style>
